import { FC, memo, useCallback, useMemo, useState } from 'react'

import { FocusNode } from '@please/lrud'
import { useTranslation } from 'react-i18next'

import { List } from '@/components/List/List'
import { focusIds, orientation } from '@/constants/focus'
import { ScrollContainer } from '@/containers/styles'
import { useCustomFocus } from '@/hooks/useCustomFocus'
import { TileType } from '@/types/servicesTypes/configTypes'
import { CatalogueType } from '@/types/servicesTypes/pageTypes'
import { scrollOptionsType } from '@/types/sliderTypes'

export const CatalogueRaw: FC<{
  catalogue: CatalogueType
  focusIdPrefix?: string
  initFocusId?: string
  anchorId?: string
  isPopup?: boolean
  scrollOptions: scrollOptionsType
}> = ({
  catalogue,
  focusIdPrefix = '',
  initFocusId,
  anchorId,
  scrollOptions,
}) => {
  const { t } = useTranslation()
  const pageFocusId = `${focusIdPrefix}_${focusIds.CATALOGUE}`
  const [activeColumn, setActiveColumn] = useState(0)

  const { defaultFocusChild, onMove } = useCustomFocus({
    startFocusChild:
      anchorId === `${focusIdPrefix}_${focusIds.CATALOGUE}` ? 1 : 0,
    focusId: pageFocusId,
    scrollOptions: scrollOptions,
  })

  const [highlightsData, catalogueData] = useMemo(() => {
    return catalogue.tiles.reduce<[TileType[], TileType[]]>(
      (acc, tile) => {
        catalogue.highlightedIds.includes(tile.id || '')
          ? acc[0].push(tile)
          : acc[1].push(tile)
        return acc
      },
      [[], []],
    )
  }, [catalogue.tiles, catalogue.highlightedIds])

  const getCatalogueData = useCallback(async () => {
    return {
      items: catalogueData,
      totalCount: catalogueData.length,
      next: '',
    } as PaginatedData<TileType>
  }, [catalogueData])

  const getHighlightsData = useCallback(async () => {
    return {
      items: highlightsData,
      totalCount: highlightsData.length,
      next: '',
    } as PaginatedData<TileType>
  }, [highlightsData])

  return (
    <FocusNode
      focusId={pageFocusId}
      elementType="section"
      orientation={orientation.VERTICAL}
      onMove={onMove}
      defaultFocusChild={defaultFocusChild}
    >
      <ScrollContainer
        id={scrollOptions.containerId}
        height={scrollOptions.height}
        $paddingBottom={scrollOptions.paddingBottom}
        $withMargin
      >
        {!!highlightsData.length && (
          <List
            pageFocusId={pageFocusId}
            listContextId={`${focusIdPrefix}_Highlights`}
            listTitle={
              catalogue.highlightTitle || catalogue.highlight || t('highlights')
            }
            fetchData={getHighlightsData}
            activeTileIndex={0}
            activeColumn={activeColumn}
            setActiveColumn={setActiveColumn}
            activeTileId={initFocusId}
            scrollOptions={scrollOptions}
            isCatalogue
          />
        )}
        {!!catalogueData.length && (
          <List
            pageFocusId={pageFocusId}
            listContextId={`${focusIdPrefix}_${focusIds.CATALOGUE}`}
            listTitle={catalogue.title}
            fetchData={getCatalogueData}
            activeTileIndex={0}
            activeColumn={activeColumn}
            setActiveColumn={setActiveColumn}
            activeTileId={initFocusId}
            scrollOptions={scrollOptions}
            isCatalogue
          />
        )}
      </ScrollContainer>
    </FocusNode>
  )
}

export const Catalogue = memo(CatalogueRaw)
